import {PlaceOrderSide} from '@px/enum';

/**
 * 根据 predicate 划分为两个部分
 */
function partition<T>(items: T[], predicate: (T, index) => boolean): [T[], T[]] {
  return items.reduce<[T[], T[]]>(
    (p, c, index) => {
      predicate(c, index) ? p[0].push(c) : p[1].push(c);
      return p;
    },
    [[], []]
  );
}

/**
 * X 方向是否重叠
 * @param a
 * @param b
 */
function isOverlapX(a: Bound, b: Bound): boolean {
  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x + a.w, b.x + b.w);
  return right - left < a.w + b.w;
}

/**
 * 从左到右，从上到下
 * @param a
 * @param b
 */
function sorter(a: GridItem, b: GridItem): number {
  if (a.y === b.y) {
    return a.x - b.x;
  }
  return a.y - b.y;
}

function sortItems(items: GridItem[]): GridItem[] {
  return items.sort(sorter);
}

function calcY(beforeItems: GridItem[], bound: Bound): number {
  const overlapItems = beforeItems.filter((o) => isOverlapX(o, bound)).map((o) => o.y + o.h);
  return Math.max(...overlapItems, 0);
}

function verticalCompact(pendingItems: GridItem[]): GridItem[] {
  return pendingItems.reduce<GridItem[]>((p, item) => p.concat({...item, y: calcY(p, item)}), []);
}

export function compactItems(items: GridItem[]): GridItem[] {
  const [visibleItems, hideItems] = partition(items, (o) => o.visible);
  return verticalCompact(sortItems(visibleItems)).concat(hideItems);
}

export function polyfillGridItems(items: GridItem[]): GridItem[] {
  return items.map((item, index) => {
    return {
      index,
      zIndex: 1,
      visible: true,
      full: false,
      static: false,
      resizable: !item.static,
      draggable: !item.static,
      min: item.min || null,
      max: item.max || null,
      drag: null,
      resize: null,
      responsive: {valueW: 0},
      ...item,
    };
  });
}

export function compactDragItems(items: GridItem[], dragItem, x: number, y: number, extend: any): GridItem[] {
  const [visibleItems, hideItems] = partition(items, (o) => o.visible);
  const otherItems = visibleItems.filter((o) => o.id !== dragItem.id);
  const compactedOtherItems = verticalCompact(sortItems(otherItems));
  const sortedItems = sortItems(compactedOtherItems);
  // y 轴方向距离 > 2 时，都放置到 dragItem 下面
  const [beforeItems, afterItems] = partition(sortedItems, (o) => y >= o.y + 2);
  const newY = calcY(beforeItems, {x, y: 0, w: dragItem.w, h: 0});
  return verticalCompact([...beforeItems, {...dragItem, x, y: newY, ...extend}, ...afterItems]).concat(hideItems);
}

/********* GridLayout start  ***********/

export function calcShadowStyle({
  shadow,
  useTransform,
  xPerPx,
  yPerPx,
  gapX,
  gapY,
  placeOrderSide,
}: {
  shadow: Shadow;
  useTransform: boolean;
  xPerPx: number;
  yPerPx: number;
  gapX: number;
  gapY: number;
  placeOrderSide: PlaceOrderSide;
}): string {
  const {drag} = shadow;
  const padding = 1;
  const x = Math.round(drag ? drag.x : shadow.x * xPerPx + (placeOrderSide === PlaceOrderSide.Left ? gapX : 0)) - padding;
  const y = Math.round(drag ? drag.y : shadow.y * yPerPx + gapY) - padding;
  const w = Math.round(shadow.w * xPerPx - gapX - shadow.responsive.valueW * xPerPx) + padding * 2;
  const h = Math.round(shadow.h * yPerPx - gapY) + padding * 2;
  return [useTransform && `transform: translate(${x}px,${y}px)`, !useTransform && `left: ${x}px`, !useTransform && `top: ${y}px`, `width:${w}px`, `height:${h}px`].filter(Boolean).join(';');
}

export function createShadow({x, y, w, h, drag, responsive}: GridItem): Shadow {
  return {x, y, w, h, drag, responsive};
}

export function calcBound(container: HTMLDivElement, ...watchItems): DOMRect {
  return container ? container.getBoundingClientRect() : null;
}

export function getRowsCount(items: GridItem[]): number {
  const visibleItems = items.filter((o) => o.visible);
  return Math.max(...visibleItems.map((item) => item.y + item.h), 1);
}

export function updateItem(items: GridItem[], item: GridItem, extend): GridItem[] {
  const index = items.findIndex((o) => o.id === item.id);
  if (index < 0) {
    return items;
  }
  const newItem = {...items[index], ...extend};
  items.splice(index, 1, newItem);
  return [...items];
}

export function calcWH(min: number, max: number, h: number): number {
  const tmp = min ? Math.max(h, min) : h;
  return max ? Math.min(tmp, max) : tmp;
}
/********* GridLayout end  ***********/

/********* GridLayoutItem start  ***********/

export function calcStyle({
  item,
  useTransform,
  xPerPx,
  yPerPx,
  gapX,
  gapY,
  placeOrderSide,
}: {
  item: GridItem;
  useTransform: boolean;
  xPerPx: number;
  yPerPx: number;
  gapX: number;
  gapY: number;
  placeOrderSide: PlaceOrderSide;
}): string {
  const {drag, resize} = item;
  const x = Math.round(drag ? drag.x : item.x * xPerPx + (placeOrderSide === PlaceOrderSide.Left ? gapX : 0));
  const y = Math.round(drag ? drag.y : item.y * yPerPx + gapY);
  const w = Math.round(resize ? resize.w : item.w * xPerPx - gapX - item.responsive.valueW * xPerPx);
  const h = Math.round(resize ? resize.h : item.h * yPerPx - gapY);
  const opacity = drag || resize ? 0.8 : 1;
  const zIndex = drag || resize ? 1000 : item.zIndex;
  return [
    useTransform && `transform:translate(${x}px,${y}px)`,
    !useTransform && `left:${x}px`,
    !useTransform && `top:${y}px`,
    `width:${w}px`,
    `height:${h}px`,
    `opacity:${opacity}`,
    `z-index:${zIndex}`,
  ]
    .filter(Boolean)
    .join(';');
}

export function getCoordinates(event) {
  const pageX = event.changedTouches ? event.changedTouches[0].pageX : event.pageX;
  const pageY = event.changedTouches ? event.changedTouches[0].pageY : event.pageY;
  return {pageX, pageY};
}

export function getTranslate(str: string): Pos {
  str = str.slice(10, -3);
  const getIndex = str.indexOf('px,');
  const x = +str.slice(0, getIndex);
  const y = +str.slice(getIndex + 4);
  return {x, y};
}

/********* GridLayoutItem end ***********/
